import React, { useCallback, useEffect, useRef, useState } from 'react'
// AGC: MiniKit import for Base App integration (v5.0.1)
import { useMiniKit } from '@coinbase/onchainkit/minikit'
// AGC: Icons removed - unused imports
import { GameGuide } from './GameGuide'
import { GameGuide2 } from './GameGuide2'
import { WelcomePage } from './WelcomePage'
// AGC: WalletConnect and FarcasterUser removed - unused imports
import { QuickBattle } from './PVEBattle'
import { SimulationBattle } from './SimulationBattle'
import { NewSimulation } from './NewSimulation'
import { DirectBattle } from './DirectBattle'
import { P2PBattle } from './P2PBattle'
import { Marketplace } from './Marketplace'
import { Inventory } from './Inventory'
import { Profile } from './Profile'
import { Games } from './Games'
import { PrivateRoom } from './PrivateRoom'
import { PVESelection } from './PVESelection'
import { Home } from './Home'
import { Splash } from './Splash'
// AGC: HybridPvEBattlePreview removed - file does not exist
import { SuggestionForm } from './SuggestionForm'
import { SuggestionsList } from './SuggestionsList'
import { SuggestionDetail } from './SuggestionDetail'
import { AdminPanel } from './AdminPanel'
import { PlayerSettingsModal } from './modals/PlayerSettingsModal'
import { useContract } from '../hooks/useContract'
import { usePlayerStatus } from '../hooks/useOGBToken'
import { usePlayerProgression } from '../hooks/usePlayerProgression'
import { useAccount } from 'wagmi'
import { useWebSocket } from '../hooks/useWebSocket'
import { logger } from '../utils/logger'
import { buildBackendUrl } from '../utils/backendUrl'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
// AGC: cn removed - unused import
import { useBackendStatus } from '../hooks/useBackendStatus'
import { usePvERefunds } from '../hooks/useP2PRefunds'
import { TopNav } from './TopNav'
import { BottomNav, BottomNavKey } from './BottomNav'
import { Tournaments } from './tournament/Tournaments'
import { PvPTourney } from './tournament/PvPTourney'
import { QuestPanel } from './quests/QuestPanel'
import { StakingDashboard } from './StakingDashboard'
import { PurchaseHistory } from './PurchaseHistory'
import { EmergencyWithdrawStakingRevenueShare } from './emergencyWithdraw_stakingrevenueshare'
import { EmergencyWithdrawStakingManager } from './emergencyWithdraw_stakingmanager'
import { Whitepaper as Whitepaper2 } from './Whitepaper2'

interface BattleParticipantSummary {
  address: string | null
  level: number | null
  isBot: boolean
  xp: number | null
  energy: number | null
}

interface RecentBattleSummary {
  battleId: string
  battleType: string
  mode: string | null
  isDraw: boolean
  createdAt: number | null
  endedAt: number | null
  timestamp: number | null
  turns: number | null
  battleLogLength: number | null
  winnerAddress: string | null
  loserAddress: string | null
  resolutionTxHash: string | null
  creationTxHash: string | null
  player1XP: number | null
  player2XP: number | null
  winnerXP: number | null
  loserXP: number | null
  drawXP: number | null
  difficulty: string | null
  player1: BattleParticipantSummary | null
  player2: BattleParticipantSummary | null
  source?: string
}

const RECENT_BATTLE_LIMIT = 50

export type ActivePage = 'home' | 'games' | 'privateRoom' | 'quickBattle' | 'p2pBattle' | 'marketplace' | 'inventory' | 'profile' | 'suggestionForm' | 'pveSelection' | 'tournament' | 'tournamentMatch' | 'quest' | 'staking' | 'purchaseHistory' | 'emergencyWithdrawStakingManager' | 'emergencyWithdrawStakingRevenueShare' | 'newSimulation' | 'whitepaper' | 'gameGuide' | 'gameGuide2'

// AGC: HomePanel and GAME_VERSION removed - unused

interface MainMenuProps {
  // AGC: Props interface for MainMenu component
}

// AGC: Main menu component for Origami Battles
// AGC: Provides navigation to different game modes and features
export const MainMenu: React.FC<MainMenuProps> = () => {
  // AGC: userInfo and isLoading - setters are used, values intentionally unused
  const [_userInfo, setUserInfo] = useState<any>(null)
  const [_isLoading, setIsLoading] = useState(true)
  const [_showGameGuide, _setShowGameGuide] = useState(false) // AGC: GameGuide is now a page, not a modal
  const [showWelcomePage, setShowWelcomePage] = useState(false)
  const [walletAddress, _setWalletAddress] = useState<string>('') // AGC: walletAddress used in JSX, setter unused
  const [showDirectBattle, setShowDirectBattle] = useState(false) // AGC: Direct battle mode
  const [activePage, setActivePage] = useState<ActivePage>('home') // AGC: Track which full-screen page is active
  const [p2pAutoStart, setP2PAutoStart] = useState(false) // AGC: Track if P2P should auto-start search (only from Home)
  const [showSimulation, setShowSimulation] = useState(false) // AGC: Simulation battle modal
  // AGC: totalBurned and isBattlePanelExpanded - setters are used, values intentionally unused
  const [_totalBurned, setTotalBurned] = useState<string>('0')
  const [_isBattlePanelExpanded, _setIsBattlePanelExpanded] = useState<boolean>(false)
  // AGC: SuggestionForm is now a page, not a modal - removed showSuggestionForm state
  const [showSuggestionsList, setShowSuggestionsList] = useState(false) // AGC: Suggestions list modal
  const [selectedSuggestion, setSelectedSuggestion] = useState<any>(null) // AGC: Selected suggestion for detail view
  const [showAdminPanel, setShowAdminPanel] = useState(false) // AGC: Admin panel modal
  
  // AGC: Emergency withdraw now uses two separate pages - no localStorage sync needed
  
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [activeNav, setActiveNav] = useState<BottomNavKey>('home') // AGC: v5.5.0 - Shared bottom nav state
  const [showGameLauncher, setShowGameLauncher] = useState(false) // AGC: v5.4.1 - Game mode selection modal
  const [showSplash, setShowSplash] = useState(true) // AGC: v5.5.0 - Show splash on initial load only
  const [showOnChainWarning, setShowOnChainWarning] = useState(false) // AGC: On-chain warning modal
  const [quickBattleModeData, setQuickBattleModeData] = useState<{ battleId: string; difficulty: string; selectedBuffs: number[]; playerLevel: number } | null>(null) // AGC: Data for mode selection
  const [showBattlePreview, setShowBattlePreview] = useState(false) // AGC: Battle preview mode
  const [tournamentMatchData, setTournamentMatchData] = useState<{ tournamentId: string; opponentAddress: string; opponentLevel: number; round: number; matchNumber: number; selectedBuffs?: number[]; battleId?: string } | null>(null) // AGC: Tournament match data
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null) // AGC: Preserve selected tournament ID when navigating
  
  // AGC: P2P RECONNECTION - Track pending match for minimize indicator in TopNav
  const [p2pPendingMatch, setP2pPendingMatch] = useState<{
    battleId: string
    opponentAddress: string
    opponentLevel: number
    hasPaid: boolean
    timestamp: number
    expiresAt: number
  } | null>(null)
  
  // AGC: P2P RECONNECTION - Track active battle for in-battle minimize indicator in TopNav
  const [p2pActiveBattle, setP2pActiveBattle] = useState<{
    battleId: string
    opponentAddress: string
    opponentLevel: number
    selectedBuffs: number[]
    selectedPotion: number | null
    currentTurn: number
    turnTimeLeft: number
    timestamp: number
  } | null>(null)
  
  // AGC: Feature flags to adapt behavior between desktop and Base App
  const { isAdminEnabled, isBaseAppContext } = useFeatureFlags()
  
  // AGC: v5.4.1 - Backend status for navbar (values intentionally unused)
  const { isOnline: _isOnline, isChecking: _isChecking } = useBackendStatus()
  
  // AGC: MiniKit hook for Base App (v5.0.1) - always call hook (OnchainKitProvider is always present)
  // AGC: CRITICAL: OnchainKitProvider is always rendered (with miniKit.enabled: false in desktop mode)
  // AGC: This allows useMiniKit() to be called safely, but it will throw if MiniKit is not enabled
  // AGC: The localhost check in farcaster-detection.ts ensures isBaseAppContext is false for localhost
  // AGC: So useMiniKit() should only be called when MiniKit is actually enabled (Base App mode)
  const miniKit = isBaseAppContext ? useMiniKit() : { setFrameReady: () => {}, isFrameReady: false }
  const { setFrameReady, isFrameReady } = miniKit

  // AGC: Use wagmi hooks for wallet connection
  const { address, isConnected } = useAccount()
  const { socket } = useWebSocket()
  
  // AGC: Use OGBToken hooks for game interactions
  const { balance: _ogbBalance, canBattle: canPlayBattle } = usePlayerStatus(address)
  
  // AGC: Use player progression for level tracking
  const { playerStats } = usePlayerProgression(address || '')
  
  // AGC: Check refunds only when entering MainMenu (not auto-checking)
  const { refresh: refreshRefunds } = usePvERefunds(address || undefined, false)

  // AGC: P2P RECONNECTION - Monitor sessionStorage for pending match and active battle (for minimize indicators)
  useEffect(() => {
    const checkP2PState = () => {
      // AGC: Check for pending match (pre-battle)
      try {
        const savedMatch = sessionStorage.getItem('p2pPendingMatch')
        if (savedMatch) {
          const matchData = JSON.parse(savedMatch)
          const age = Date.now() - (matchData.timestamp || 0)
          const MATCH_TIMEOUT = 3 * 60 * 1000 // 3 minutes
          
          if (age < MATCH_TIMEOUT) {
            setP2pPendingMatch({
              battleId: matchData.battleId,
              opponentAddress: matchData.opponentAddress,
              opponentLevel: matchData.opponentLevel,
              hasPaid: matchData.hasPaid || false,
              timestamp: matchData.timestamp,
              expiresAt: matchData.timestamp + MATCH_TIMEOUT
            })
          } else {
            // AGC: Match expired - clear it
            sessionStorage.removeItem('p2pPendingMatch')
            setP2pPendingMatch(null)
          }
        } else {
          setP2pPendingMatch(null)
        }
      } catch (error) {
        logger.warn('Failed to read p2pPendingMatch from sessionStorage:', error)
        setP2pPendingMatch(null)
      }
      
      // AGC: Check for active battle (in-battle)
      try {
        const savedBattle = sessionStorage.getItem('p2pActiveBattle')
        if (savedBattle) {
          const battleData = JSON.parse(savedBattle)
          const age = Date.now() - (battleData.timestamp || 0)
          const BATTLE_TIMEOUT = 10 * 60 * 1000 // 10 minutes (battles shouldn't last longer)
          
          if (age < BATTLE_TIMEOUT) {
            setP2pActiveBattle({
              battleId: battleData.battleId,
              opponentAddress: battleData.opponentAddress,
              opponentLevel: battleData.opponentLevel,
              selectedBuffs: battleData.selectedBuffs || [],
              selectedPotion: battleData.selectedPotion || null,
              currentTurn: battleData.currentTurn || 1,
              turnTimeLeft: battleData.turnTimeLeft || 60,
              timestamp: battleData.timestamp
            })
          } else {
            // AGC: Battle expired - clear it
            sessionStorage.removeItem('p2pActiveBattle')
            setP2pActiveBattle(null)
          }
        } else {
          setP2pActiveBattle(null)
        }
      } catch (error) {
        logger.warn('Failed to read p2pActiveBattle from sessionStorage:', error)
        setP2pActiveBattle(null)
      }
    }
    
    // AGC: Check immediately and on interval
    checkP2PState()
    const intervalId = setInterval(checkP2PState, 1000)
    
    // AGC: Also listen for storage events (when sessionStorage changes in another tab/component)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'p2pPendingMatch' || e.key === 'p2pActiveBattle') {
        checkP2PState()
      }
    }
    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      clearInterval(intervalId)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // AGC: v5.4.1 - Computed values for top navbar
  // AGC: networkLabel, serverStatusLabel, serverStatusClass, ogbBalanceDisplay, profileInitial removed - unused

  // AGC: Track recent battles feed from backend (global history) - value unused (setter used, value may be used in future)
  const [_recentBattles, setRecentBattles] = useState<RecentBattleSummary[]>([])
  // AGC: Values prefixed with _ to indicate intentionally unused (setters are used)
  const [_recentBattlesLoading, setRecentBattlesLoading] = useState<boolean>(false)
  const [_recentBattlesError, setRecentBattlesError] = useState<string | null>(null)

  const recentBattlesCancelledRef = useRef(false)

  const fetchRecentBattles = useCallback(async ({ showSpinner = true } = {}) => {
    if (recentBattlesCancelledRef.current) {
      return
    }

    try {
      if (showSpinner) {
        setRecentBattlesLoading(true)
      }
      setRecentBattlesError(null)

      const response = await fetch(buildBackendUrl(`api/battles/recent?limit=${RECENT_BATTLE_LIMIT}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      if (!recentBattlesCancelledRef.current && data && Array.isArray(data.battles)) {
        setRecentBattles(data.battles as RecentBattleSummary[])
      }
    } catch (error) {
      logger.error('Failed to load recent battles:', error)
      if (!recentBattlesCancelledRef.current) {
        setRecentBattlesError('Unable to load recent battles')
      }
    } finally {
      if (!recentBattlesCancelledRef.current) {
        setRecentBattlesLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    recentBattlesCancelledRef.current = false
    fetchRecentBattles({ showSpinner: true })
    const intervalId = setInterval(() => fetchRecentBattles({ showSpinner: false }), 15000)

    return () => {
      recentBattlesCancelledRef.current = true
      clearInterval(intervalId)
    }
  }, [fetchRecentBattles])

  // AGC: Check refunds when entering MainMenu (home page) or when address changes
  useEffect(() => {
    if (address && activePage === 'home') {
      logger.debug('MainMenu: Checking refunds on home page entry...')
      refreshRefunds()
    }
  }, [address, activePage, refreshRefunds])

  useEffect(() => {
    const handleRefresh = () => fetchRecentBattles({ showSpinner: false })
    window.addEventListener('origami:refresh-recent-battles', handleRefresh)
    return () => {
      window.removeEventListener('origami:refresh-recent-battles', handleRefresh)
    }
  }, [fetchRecentBattles])

  // AGC: Handle URL hash routing for admin panel and battle preview
  useEffect(() => {
    const hash = window.location.hash
    
    // AGC: Battle preview mode (for testing UI without playing)
    if (hash === '#battle-preview') {
      setShowBattlePreview(true)
      return
    }
    
    // AGC: Force admin panel to open if #admin is in URL, even if detection is wrong
    // AGC: This allows admin access during development when context detection might be incorrect
    if (hash === '#admin') {
      if (!isAdminEnabled) {
        logger.warn('AGC: Admin panel requested but isAdminEnabled is false. Opening anyway for development.')
      }
      setShowAdminPanel(true)
      return
    }
    
    // AGC: Only auto-open if admin is enabled (normal flow)
    if (!isAdminEnabled) {
      return
    }
  }, [isAdminEnabled])

  // AGC: Update URL hash when admin panel opens/closes
  useEffect(() => {
    // AGC: Don't close admin panel if #admin is in URL (allows forced access)
    const hash = window.location.hash
    if (!isAdminEnabled && hash !== '#admin') {
      if (showAdminPanel) {
        setShowAdminPanel(false)
      }
      return
    }

    if (showAdminPanel) {
      window.location.hash = 'admin'
    } else if (window.location.hash === '#admin') {
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [isAdminEnabled, showAdminPanel])

  // AGC: isBotAddress and formatAddress removed - were only used in removed _formatParticipant function

  // AGC: formatParticipant removed - unused (was kept for potential future use but never used)

  // AGC: Unused format functions removed - were from older version of component

  // AGC: Use contract hooks for other interactions
  const { refreshOGBStatus, getTotalBurned } = useContract()


  // AGC: Auto-connect wallet in Base App context using MiniKit (v5.0.1)
  // AGC: MiniKit handles wallet connection automatically - no manual connector logic needed
  useEffect(() => {
    if (!isBaseAppContext) {
      // AGC: Skip in desktop mode (RainbowKit handles wallet connection)
      return
    }

    logger.debug('[MiniApp] AGC: Base App mode - MiniKit handles wallet auto-connect internally')
  }, [isBaseAppContext])

  // AGC: Call setFrameReady() ONLY in Base App context (v5.0.1)
  useEffect(() => {
    if (!isBaseAppContext) {
      // AGC: Skip logging in desktop mode to reduce console noise
      return
    }

    if (isFrameReady) {
      // AGC: Frame already ready, no need to call again
      return
    }

    logger.debug('[MiniApp] AGC: Base App mode - MainMenu mounted, calling setFrameReady()')
    
    setFrameReady()
    logger.info('[OK] AGC: setFrameReady() called - splash screen dismissed!')
  }, [isBaseAppContext, isFrameReady, setFrameReady])

  useEffect(() => {
    // AGC: CRITICAL FIX - Only run ONCE on mount to prevent infinite context loop
    // AGC: This prevents flooding the context API

    // AGC: Get user context (placeholder for future MiniKit context integration)
    const getUserInfo = async () => {
      try {
        // AGC: MiniKit context integration to be implemented
        // AGC: For now, user info comes from wallet connection
        setUserInfo(null)
      } catch (error) {
        logger.debug('User context unavailable')
      } finally {
        setIsLoading(false)
      }
    }

    // AGC: Load total burned tokens (non-blocking)
    const loadBurnedTokens = async () => {
      try {
        const burned = await getTotalBurned()
        setTotalBurned(burned)
      } catch (error: any) {
        // AGC: Provide user-friendly message for rate limiting
        if (error?.message?.includes('rate limit')) {
          logger.warn('RPC rate limited - burned tokens will retry automatically')
          setTotalBurned('...')
        } else {
          logger.debug('Burned tokens unavailable (expected on fresh start)')
          setTotalBurned('0')
        }
      }
    }

    getUserInfo()
    loadBurnedTokens()
  }, []) // AGC: Empty deps - run ONCE on mount (prevents context flood)

  // AGC: Handle wallet connection
  // AGC: handleWalletConnected removed - unused

  const pageToNav = useCallback((page: ActivePage): BottomNavKey => {
    if (page === 'games' || page === 'privateRoom' || page === 'quickBattle' || page === 'p2pBattle' || page === 'pveSelection' || page === 'tournament' || page === 'newSimulation') {
      return 'games'
    }
    if (page === 'marketplace') {
      return 'market'
    }
    if (page === 'quest') {
      return 'quest'
    }
    if (page === 'staking') {
      return 'staking'
    }
    if (page === 'inventory' || page === 'profile') {
      return 'quest' // AGC: Profile page now shows quest nav as active
    }
    if (page === 'suggestionForm' || page === 'whitepaper' || page === 'gameGuide' || page === 'gameGuide2') {
      return 'home' // AGC: SuggestionForm, Whitepaper, GameGuide, and GameGuide2 show home nav as active
    }
    return 'home'
  }, [])

  const navigateToPage = useCallback((page: ActivePage) => {
    // AGC: Track if navigating to P2P from Home (for auto-start search)
    if (page === 'p2pBattle' && activePage === 'home') {
      setP2PAutoStart(true)
    } else if (page === 'p2pBattle') {
      // AGC: Navigating from game launcher or other pages - don't auto-start
      setP2PAutoStart(false)
    }
    
    // AGC: Clear tournament selection when navigating to tournament page from Games (not from popup)
    // AGC: This ensures tournament list shows instead of auto-joining active tournament
    if (page === 'tournament' && activePage === 'games') {
      setSelectedTournamentId(null)
    }
    
    setActivePage(page)
    setActiveNav(pageToNav(page))
    // AGC: Scroll to top when navigating to battle pages to prevent scroll position issues
    if (page === 'quickBattle' || page === 'p2pBattle' || page === 'newSimulation') {
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [activePage, pageToNav])

  // AGC: Handle splash completion
  const handleSplashComplete = useCallback(() => {
    setShowSplash(false)
    navigateToPage('home')
  }, [navigateToPage])

  const handleGlobalNavClick = useCallback(
    (navId: BottomNavKey) => {
      logger.debug('[AGC] Bottom nav clicked:', navId)
      // AGC: Hide splash when navigating (only shows on initial load)
      setShowSplash(false)
      setActiveNav(navId)
      if (navId === 'home') {
        logger.debug('[AGC] Navigating to home')
        navigateToPage('home')
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } else if (navId === 'games') {
        logger.debug('[AGC] Navigating to games page')
        navigateToPage('games')
      } else if (navId === 'market') {
        logger.debug('[AGC] Navigating to marketplace')
        navigateToPage('marketplace')
      } else if (navId === 'quest') {
        logger.debug('[AGC] Navigating to quest page')
        navigateToPage('quest')
      } else if (navId === 'staking') {
        logger.debug('[AGC] Navigating to staking page')
        navigateToPage('staking')
      }
    },
    [navigateToPage],
  )

  const handleOpenInventoryPage = useCallback(() => {
    navigateToPage('inventory')
  }, [navigateToPage])

  const handleOpenMarketplacePage = useCallback(() => {
    navigateToPage('marketplace')
  }, [navigateToPage])

  // AGC: handleOpenQuickBattlePage, handleOpenP2PBattlePage, handleOpenProfilePage, handleOpenSuggestionForm removed - unused

  // AGC: Listen for on-chain warning modal request from PVESelection
  useEffect(() => {
    const handleShowWarning = (event: CustomEvent) => {
      const { mode: _mode, data } = event.detail
      setQuickBattleModeData(data)
      setShowOnChainWarning(true)
    }

    window.addEventListener('mainmenu:show-onchain-warning', handleShowWarning as EventListener)
    return () => {
      window.removeEventListener('mainmenu:show-onchain-warning', handleShowWarning as EventListener)
    }
  }, [])

  // AGC: Listen for tournament match start event from TournamentLobby
  useEffect(() => {
    const handleTournamentMatch = (event: CustomEvent) => {
      const matchData = event.detail
      console.log('🏆 Tournament match started:', matchData)
      setTournamentMatchData(matchData)
      navigateToPage('tournamentMatch')
    }

    window.addEventListener('tournament:start-match', handleTournamentMatch as EventListener)
    return () => {
      window.removeEventListener('tournament:start-match', handleTournamentMatch as EventListener)
    }
  }, [navigateToPage])

  // AGC: Listen for navigation to purchase history from StakingDashboard
  useEffect(() => {
    const handleNavigateToPurchaseHistory = () => {
      navigateToPage('purchaseHistory')
    }
    window.addEventListener('mainmenu:navigate-to-purchase-history', handleNavigateToPurchaseHistory)
    return () => {
      window.removeEventListener('mainmenu:navigate-to-purchase-history', handleNavigateToPurchaseHistory)
    }
  }, [navigateToPage])

  // AGC: Listen for tour navigation events (e.g., Battle Tour needs Games page or QuickBattle page)
  useEffect(() => {
    const handleTourNavigation = (event: CustomEvent) => {
      const { page } = event.detail
      // AGC: Allow navigation to any valid page for tour purposes
      const validTourPages = ['games', 'home', 'marketplace', 'quest', 'staking', 'quickBattle', 'p2pBattle', 'inventory', 'profile']
      if (page && validTourPages.includes(page)) {
        logger.debug(`[Tour] Navigating to ${page} for tour`)
        navigateToPage(page as ActivePage)
      }
    }
    window.addEventListener('tour:navigate-to-page', handleTourNavigation as EventListener)
    return () => {
      window.removeEventListener('tour:navigate-to-page', handleTourNavigation as EventListener)
    }
  }, [navigateToPage])
  
  // AGC: FIX #7: Listen for tournament battle start from WebSocket (match_found)
  useEffect(() => {
    const handleTournamentBattleStart = (event: CustomEvent) => {
      const matchData = event.detail
      console.log('🏆 Tournament battle found (WebSocket):', matchData)
      
      // AGC: CRITICAL FIX - Don't navigate if we're already on tournament lobby or if battle is completed
      // AGC: This prevents auto-refresh back to completed battles
      if (activePage === 'tournament' || activePage === 'tournamentMatch') {
        console.log('⏭️  Ignoring match_found - already on tournament page:', activePage)
        return
      }
      
      // AGC: Convert WebSocket format to TournamentMatchData format
      setTournamentMatchData({
        tournamentId: matchData.tournamentId,
        opponentAddress: matchData.opponentAddress,
        opponentLevel: matchData.opponentLevel,
        round: matchData.round || 1,
        matchNumber: matchData.matchNumber || 1,
        battleId: matchData.battleId
      })
      navigateToPage('tournamentMatch')
    }

    window.addEventListener('tournament-battle-start', handleTournamentBattleStart as EventListener)
    return () => {
      window.removeEventListener('tournament-battle-start', handleTournamentBattleStart as EventListener)
    }
  }, [navigateToPage, activePage])

  // AGC: Listen for tour mode selection events (step 13)
  useEffect(() => {
    const handleTourSelectPvE = () => {
      console.log('AGC: Tour selected PvE mode - starting battle')
      // AGC: The Start Battle button click will handle the rest
      // AGC: Dispatch battle started event after a delay (when battle actually starts)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('battle:started'))
      }, 3000) // AGC: Wait 3 seconds for transactions to process
    }
    
    const handleTourSelectSimulation = () => {
      console.log('AGC: Tour selected Simulation mode')
      // AGC: Navigate to newSimulation page instead of opening old modal
      navigateToPage('newSimulation')
      // AGC: Dispatch simulation started event (for tour step 20)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('simulation:started'))
      }, 1000)
    }

    window.addEventListener('tour:select-pve-mode', handleTourSelectPvE as EventListener)
    window.addEventListener('tour:select-simulation-mode', handleTourSelectSimulation as EventListener)
    
    return () => {
      window.removeEventListener('tour:select-pve-mode', handleTourSelectPvE as EventListener)
      window.removeEventListener('tour:select-simulation-mode', handleTourSelectSimulation as EventListener)
    }
  }, [navigateToPage])

  // AGC: Force the Base App container to refresh after modals close (fixes blank frame issue)
  const forceMainMenuRefresh = useCallback(() => {
    // AGC: CRITICAL FIX (v5.0.1) - DO NOT call setFrameReady() multiple times!
    // AGC: Calling ready() more than once crashes runtime
    // AGC: Instead, just dispatch resize event to refresh the frame
    
    if (isBaseAppContext) {
      logger.debug('[MiniApp] AGC: Base App context - dispatching resize event (NOT calling ready())')
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'))
        // AGC: Force a small layout recalc to ensure frame updates
        document.body.style.height = `${window.innerHeight}px`
      })
    } else {
      // AGC: Desktop mode - just dispatch resize
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'))
      })
    }
  }, [isBaseAppContext])

  // AGC: CRITICAL FIX - Safe close functions that ensure main menu re-renders
  const safeCloseModal = useCallback(
    (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
      setter(false)
      logger.debug('[UI] Modal closed, scheduling main menu refresh')
      forceMainMenuRefresh()
    },
    [forceMainMenuRefresh]
  )

  return (
    <>
      {/* AGC: v5.5.0 - Persistent app container with fixed top/bottom nav (always visible) */}
      <div className="min-h-screen w-full bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
        {/* AGC: v5.5.0 - FIXED Top Navbar (always visible) */}
        <TopNav 
          onOpenSuggestion={() => navigateToPage('suggestionForm')}
          onOpenProfile={() => navigateToPage('profile')}
          onOpenSettings={() => setIsSettingsOpen(true)}
          onOpenInventory={handleOpenInventoryPage}
          onOpenTournament={() => navigateToPage('tournament')}
          onSelectTournament={setSelectedTournamentId}
          activePage={activePage}
          onOpenEmergencyWithdrawStakingManager={() => navigateToPage('emergencyWithdrawStakingManager')}
          onOpenEmergencyWithdrawStakingRevenueShare={() => navigateToPage('emergencyWithdrawStakingRevenueShare')}
          onOpenP2PBattle={() => navigateToPage('p2pBattle')}
          p2pPendingMatch={p2pPendingMatch}
          p2pActiveBattle={p2pActiveBattle}
        />

        {/* AGC: v5.5.0 - DYNAMIC Middle Content (changes based on activePage) */}
        {/* AGC: Show Splash on initial load (only when on home page), then Home after splash completes */}
        {/* AGC: Preload Home invisibly while splash is showing to eliminate delay */}
        {showSplash && activePage === 'home' && (
          <>
            {/* AGC: Render Home component but keep it hidden - splash will overlay it */}
            <div className="opacity-0 pointer-events-none absolute inset-0" aria-hidden="true">
              <Home 
                onNavigateToPage={navigateToPage}
                onShowGameGuide={() => navigateToPage('gameGuide2')}
                onOpenSuggestion={() => navigateToPage('suggestionForm')}
                onOpenEmergencyWithdrawStakingManager={() => navigateToPage('emergencyWithdrawStakingManager')}
                onOpenEmergencyWithdrawStakingRevenueShare={() => navigateToPage('emergencyWithdrawStakingRevenueShare')}
              />
            </div>
            {/* AGC: Splash screen - centered in middle content area (doesn't cover TopNav/BottomNav buttons) */}
            <Splash onComplete={handleSplashComplete} fullScreen={false} />
          </>
        )}
        {!showSplash && activePage === 'home' && (
          <Home 
            onNavigateToPage={navigateToPage}
            onShowGameGuide={() => navigateToPage('gameGuide2')}
            onOpenSuggestion={() => navigateToPage('suggestionForm')}
            onOpenEmergencyWithdrawStakingManager={() => navigateToPage('emergencyWithdrawStakingManager')}
            onOpenEmergencyWithdrawStakingRevenueShare={() => navigateToPage('emergencyWithdrawStakingRevenueShare')}
          />
        )}

        {/* AGC: v5.5.0 - Marketplace page content (renders inside fixed nav shell) */}
        {activePage === 'marketplace' && (
          <main className="pt-[72px] pb-[88px] px-3">
            <Marketplace
              onNavigateToPage={navigateToPage}
              onNavClick={handleGlobalNavClick}
              activeNav={activeNav}
              onOpenSuggestion={() => navigateToPage('suggestionForm')}
              onOpenSettings={() => setIsSettingsOpen(true)}
              onOpenInventory={handleOpenInventoryPage}
            />
          </main>
        )}

        {/* AGC: v5.5.0 - Inventory page content */}
        {activePage === 'inventory' && (
          <main className="pt-[72px] pb-[88px] px-3">
            <Inventory
              playerAddress={address || walletAddress || '0x70997970c51812dc3a010c7d01b50e0d17dc79c8'}
              onNavigateToMarketplace={handleOpenMarketplacePage}
            />
          </main>
        )}

        {/* AGC: v5.5.0 - Profile page content */}
        {activePage === 'profile' && (
          <>
            <Profile
              playerAddress={address || walletAddress || '0x70997970c51812dc3a010c7d01b50e0d17dc79c8'}
              onNavigateToPage={navigateToPage}
              onNavClick={handleGlobalNavClick}
              activeNav={activeNav}
              onOpenSuggestion={() => navigateToPage('suggestionForm')}
              onOpenSettings={() => setIsSettingsOpen(true)}
              onOpenInventory={handleOpenInventoryPage}
            />
          </>
        )}

        {/* AGC: v6.0.0 - Quest page content (Origami Battle 2.0) */}
        {activePage === 'quest' && (
          <main className="pt-[72px] pb-[88px] px-3">
            <QuestPanel />
          </main>
        )}

        {/* AGC: v6.0.0 - Staking page content (Origami Battle 2.0) */}
        {activePage === 'staking' && (
          <main className="pt-[72px] pb-[88px] px-3">
            <StakingDashboard />
          </main>
        )}

        {activePage === 'purchaseHistory' && (
          <main className="pt-[72px] pb-[88px] px-3">
            <PurchaseHistory 
              onBack={() => navigateToPage('staking')}
            />
          </main>
        )}

        {/* AGC: Emergency Withdraw Page - StakingManager (2-of-2 signer approval) */}
        {activePage === 'emergencyWithdrawStakingManager' && (
          <EmergencyWithdrawStakingManager 
            key="staking-manager"
            onBack={() => navigateToPage('home')}
          />
        )}

        {/* AGC: Emergency Withdraw Page - StakingRevenueShare (1 signer approval) */}
        {activePage === 'emergencyWithdrawStakingRevenueShare' && (
          <EmergencyWithdrawStakingRevenueShare 
            key="staking-revenue-share"
            onBack={() => navigateToPage('home')}
          />
        )}

        {/* AGC: Whitepaper page content */}
        {activePage === 'whitepaper' && (
          <Whitepaper2 onBack={() => navigateToPage('home')} />
        )}

        {/* AGC: GameGuide page content */}
        {activePage === 'gameGuide' && (
          <GameGuide 
            onBack={() => navigateToPage('home')} 
            onStartSimulation={() => navigateToPage('newSimulation')}
            onShortVersion={() => navigateToPage('gameGuide2')}
          />
        )}

        {/* AGC: GameGuide2 (Short Version) page content */}
        {activePage === 'gameGuide2' && (
          <GameGuide2 
            onFullVersion={() => navigateToPage('gameGuide')}
          />
        )}

        {/* AGC: v5.5.0 - Games page content (renders inside fixed nav shell) */}
        {activePage === 'games' && (
          <>
            <Games
              onNavigateToPage={navigateToPage}
              onNavClick={handleGlobalNavClick}
              activeNav={activeNav}
              onOpenSuggestion={() => navigateToPage('suggestionForm')}
              onOpenSettings={() => setIsSettingsOpen(true)}
              onOpenInventory={handleOpenInventoryPage}
            />
          </>
        )}

        {/* AGC: v5.5.0 - Tournaments page content (renders inside fixed nav shell) */}
        {activePage === 'tournament' && (
          <>
            <Tournaments
              onNavigateToPage={navigateToPage}
              onNavClick={handleGlobalNavClick}
              activeNav={activeNav}
              onOpenSuggestion={() => navigateToPage('suggestionForm')}
              onOpenSettings={() => setIsSettingsOpen(true)}
              onOpenInventory={handleOpenInventoryPage}
              initialTournamentId={selectedTournamentId}
              onTournamentIdChange={setSelectedTournamentId}
            />
          </>
        )}

        {/* AGC: Tournament Match page content (renders inside fixed nav shell) */}
        {activePage === 'tournamentMatch' && address && tournamentMatchData && (
          playerStats?.level ? (
            <>
              <PvPTourney
                playerAddress={address}
                playerLevel={playerStats.level}
                tournamentId={tournamentMatchData.tournamentId}
                opponentAddress={tournamentMatchData.opponentAddress}
                opponentLevel={tournamentMatchData.opponentLevel}
                matchData={{
                  round: tournamentMatchData.round,
                  matchNumber: tournamentMatchData.matchNumber
                }}
                preSelectedBuffs={tournamentMatchData.selectedBuffs}
                onNavigateToPage={navigateToPage}
                onMatchComplete={(winner, loser, turns, isDraw, battleId) => {
                  // AGC: Report match result to backend via socket
                  if (socket && tournamentMatchData && address) {
                    console.log('🏆 Reporting tournament match result:', { winner, loser, turns, isDraw, battleId })
                    socket.emit('tournament_match_complete', {
                      tournamentId: tournamentMatchData.tournamentId,
                      matchId: battleId || '', // AGC: Use battleId from match completion callback
                      winner,
                      loser,
                      player1Address: address, // AGC: Current player
                      player2Address: tournamentMatchData.opponentAddress, // AGC: Opponent
                      isDraw
                    })
                  }
                  // AGC: Preserve tournament ID when navigating back to tournament page
                  // AGC: This ensures we stay on the tournament detail view instead of going to list
                  if (tournamentMatchData?.tournamentId) {
                    setSelectedTournamentId(tournamentMatchData.tournamentId)
                  }
                  // AGC: Don't clear tournamentMatchData immediately - let TournamentLobby handle state
                  // AGC: Navigate back to tournament - TournamentLobby will reconnect to active tournament
                  navigateToPage('tournament')
                  // AGC: Clear match data after a short delay to allow navigation
                  setTimeout(() => {
                    setTournamentMatchData(null)
                  }, 1000)
                }}
              />
            </>
          ) : (
            <main className="pt-[72px] pb-[88px] px-3 flex items-center justify-center min-h-[calc(100vh-160px)]">
              <div className="bg-slate-800 p-8 rounded-lg text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-white text-lg">Loading player stats...</p>
                <p className="text-gray-400 text-sm mt-2">Please wait for your level to load</p>
              </div>
            </main>
          )
        )}

        {/* AGC: v5.5.0 - Private Room page content (renders inside fixed nav shell) */}
        {activePage === 'privateRoom' && (
          <>
            <PrivateRoom
              onNavigateToPage={navigateToPage}
              onNavClick={handleGlobalNavClick}
              activeNav={activeNav}
              onOpenSuggestion={() => navigateToPage('suggestionForm')}
              onOpenSettings={() => setIsSettingsOpen(true)}
              onOpenInventory={handleOpenInventoryPage}
            />
          </>
        )}

        {/* AGC: New Simulation Battle page (renders inside fixed nav shell) */}
        {activePage === 'newSimulation' && (
          <NewSimulation
            playerAddress={address || walletAddress || '0x70997970c51812dc3a010c7d01b50e0d17dc79c8'}
            socket={socket}
            onBackToMenu={() => navigateToPage('home')}
          />
        )}

        {/* AGC: v5.5.0 - QuickBattle page content (renders inside fixed nav shell) */}
        {activePage === 'quickBattle' && (
          <>
            <QuickBattle
              playerAddress={address || walletAddress || '0x70997970c51812dc3a010c7d01b50e0d17dc79c8'}
              onBattleComplete={async (result) => {
                logger.info('[QuickBattle] Completed:', result)
                if (refreshOGBStatus) {
                  refreshOGBStatus(result.playerAddress)
                }
                try {
                  const burned = await getTotalBurned()
                  setTotalBurned(burned)
                } catch (error) {
                  logger.error('Failed to refresh burned tokens:', error)
                }
              }}
              onNavigateToMarketplace={handleOpenMarketplacePage}
              onNavClick={handleGlobalNavClick}
              activeNav={activeNav}
              onOpenSuggestion={() => navigateToPage('suggestionForm')}
              onOpenSettings={() => setIsSettingsOpen(true)}
              onOpenInventory={handleOpenInventoryPage}
              onShowModeSelection={(data) => {
                setQuickBattleModeData(data)
                navigateToPage('pveSelection')
              }}
            />
          </>
        )}

        {/* AGC: v5.5.0 - PVE Selection page content (renders inside fixed nav shell) */}
        {activePage === 'pveSelection' && (
          <>
            <PVESelection
              onNavigateToPage={navigateToPage}
              onNavClick={handleGlobalNavClick}
              activeNav={activeNav}
              onOpenSuggestion={() => navigateToPage('suggestionForm')}
              onOpenSettings={() => setIsSettingsOpen(true)}
              onOpenInventory={handleOpenInventoryPage}
              onModeSelected={(mode, data) => {
                // AGC: Navigate first, then dispatch event after a delay to ensure QuickBattle is mounted and listening
                if (mode === 'onchain') {
                  // AGC: Show warning modal for on-chain mode
                  setShowOnChainWarning(true)
                  setQuickBattleModeData(data)
                } else {
                  // AGC: Navigate back to QuickBattle page first
                  navigateToPage('quickBattle')
                  
                  // AGC: Dispatch event after navigation with a small delay to ensure QuickBattle is mounted
                  setTimeout(() => {
                    logger.debug('[MainMenu] Dispatching quickbattle:mode-selected event:', { mode, data })
                    window.dispatchEvent(new CustomEvent('quickbattle:mode-selected', { detail: { mode, data } }))
                  }, 100)
                }
              }}
              modeData={quickBattleModeData}
            />
          </>
        )}

        {/* AGC: v5.5.0 - P2PBattle page content (renders inside fixed nav shell) */}
        {activePage === 'p2pBattle' && address && (
          playerStats?.level ? (
            <>
              <P2PBattle
                playerAddress={address}
                playerLevel={playerStats.level}
                onNavigateToPage={navigateToPage}
                onNavigateToMarketplace={handleOpenMarketplacePage}
                onNavClick={handleGlobalNavClick}
                activeNav={activeNav}
                onOpenSuggestion={() => navigateToPage('suggestionForm')}
                onOpenSettings={() => setIsSettingsOpen(true)}
                onOpenInventory={handleOpenInventoryPage}
                autoStartSearch={p2pAutoStart}
              />
            </>
          ) : (
            <main className="pt-[72px] pb-[88px] px-3 flex items-center justify-center min-h-[calc(100vh-160px)]">
              <div className="bg-slate-800 p-8 rounded-lg text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-white text-lg">Loading player stats...</p>
                <p className="text-gray-400 text-sm mt-2">Please wait for your level to load</p>
              </div>
            </main>
          )
        )}

        {/* AGC: v5.5.0 - SuggestionForm page content (renders inside fixed nav shell) */}
        {activePage === 'suggestionForm' && (
          <main className="pt-[72px] pb-[88px] px-3">
            <SuggestionForm
              onClose={() => navigateToPage('home')}
              onSubmitSuccess={() => {
                // AGC: Refresh suggestions list if it's open
                if (showSuggestionsList) {
                  // AGC: List will auto-refresh on next render
                }
              }}
              onViewAll={() => {
                navigateToPage('home')
                setShowSuggestionsList(true)
              }}
              onSuggestionClick={(suggestion) => {
                navigateToPage('home')
                setSelectedSuggestion(suggestion)
              }}
            />
          </main>
        )}


        {/* AGC: v5.5.0 - FIXED Bottom Navigation (always visible) */}
        <BottomNav onNavClick={handleGlobalNavClick} activeNav={activeNav} />
      </div>

      {/* AGC: Game Launcher Modal */}
      {showGameLauncher && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-sm space-y-3 rounded-2xl bg-slate-950 border border-slate-800 p-4">
            <div className="text-center space-y-1">
              <h3 className="text-lg font-semibold">Select Game Mode</h3>
              <p className="text-xs text-slate-400">Choose how you want to battle today</p>
    </div>
        <button
              onClick={() => {
                setShowGameLauncher(false)
                navigateToPage('quickBattle')
              }}
              className={`w-full rounded-xl border border-blue-400/40 bg-blue-600/20 px-4 py-3 text-left ${
                !isConnected || !canPlayBattle ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600/30'
              }`}
              disabled={!isConnected || !canPlayBattle}
            >
              <p className="text-sm font-semibold text-white">Quick Battle (PvE)</p>
              <p className="text-xs text-slate-300">Battle AI bots instantly · 50 OGB entry</p>
        </button>
        <button
              onClick={() => {
                setShowGameLauncher(false)
                navigateToPage('p2pBattle')
              }}
              className={`w-full rounded-xl border border-orange-400/40 bg-orange-600/10 px-4 py-3 text-left ${
                !isConnected || !canPlayBattle ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-600/20'
              }`}
              disabled={!isConnected || !canPlayBattle}
            >
              <p className="text-sm font-semibold text-white">PvP Battle</p>
              <p className="text-xs text-slate-300">Queue against real players · 50 OGB entry</p>
        </button>
        <button
              onClick={() => setShowGameLauncher(false)}
              className="w-full rounded-xl border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:text-white"
            >
              Cancel
        </button>
              </div>
            </div>
      )}


      {/* AGC: Direct Battle Modal */}
      {showDirectBattle && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <DirectBattle
              playerAddress={walletAddress || '0x70997970c51812dc3a010c7d01b50e0d17dc79c8'} // Default test address
              onBattleComplete={async (result) => {
                logger.info('[DirectBattle] Completed:', result)
                // AGC: Refresh OGB status after battle
                if (refreshOGBStatus) {
                  refreshOGBStatus(result.playerAddress)
                }
                // AGC: Refresh burned tokens display
                try {
                  const burned = await getTotalBurned()
                  setTotalBurned(burned)
                } catch (error) {
                  logger.error('Failed to refresh burned tokens:', error)
                  // Keep current value if refresh fails
                }
              }}
              onBackToMenu={() => safeCloseModal(setShowDirectBattle)}
            />
          </div>
        </div>
      )}


      {/* AGC: WelcomePage Modal */}
      {showWelcomePage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="w-full min-h-full">
            <WelcomePage
              onBackToMenu={() => setShowWelcomePage(false)}
              onStartPlaying={() => {
                setShowWelcomePage(false)
                navigateToPage('newSimulation')
              }}
            />
          </div>
        </div>
      )}

      {/* AGC: Simulation Battle Modal (with difficulty and NFT selection) */}
      {showSimulation && socket && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <SimulationBattle
              playerAddress={address || walletAddress || '0x70997970c51812dc3a010c7d01b50e0d17dc79c8'}
              socket={socket}
              onBackToMenu={() => safeCloseModal(setShowSimulation)}
            />
          </div>
        </div>
      )}

      {/* AGC: Suggestions List Modal */}
      {showSuggestionsList && !selectedSuggestion && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-4xl">
            <SuggestionsList
              onClose={() => setShowSuggestionsList(false)}
              onSuggestionClick={(suggestion) => setSelectedSuggestion(suggestion)}
            />
          </div>
        </div>
      )}

      {/* AGC: Suggestion Detail Modal */}
      {selectedSuggestion && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-4xl">
            <SuggestionDetail
              suggestion={selectedSuggestion}
              onClose={() => {
                setSelectedSuggestion(null)
                setShowSuggestionsList(false)
              }}
              onBack={() => setSelectedSuggestion(null)}
            />
          </div>
        </div>
      )}

      {/* AGC: Admin Panel - Full Screen */}
      {/* AGC: Allow admin panel to show even if isAdminEnabled is false (for development when detection is wrong) */}
      {showAdminPanel && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <AdminPanel onClose={() => setShowAdminPanel(false)} />
        </div>
      )}

      <PlayerSettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />

      {/* AGC: Battle Preview Mode - Full Screen (for testing UI) */}
      {/* AGC: Disabled - HybridPvEBattlePreview component does not exist */}
      {showBattlePreview && (
        <main className="pt-[72px] pb-[88px] px-3 min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => {
                setShowBattlePreview(false)
                window.location.hash = ''
              }}
              className="mb-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
            >
              ← Close Preview
            </button>
            <div className="p-8 text-center text-white">
              <p className="text-lg">Battle Preview Mode</p>
              <p className="text-sm text-slate-400 mt-2">Component not available</p>
            </div>
          </div>
        </main>
      )}

      {/* AGC: On-Chain Beta Warning Modal - Full Screen */}
      {showOnChainWarning && quickBattleModeData && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-full h-full flex flex-col items-center justify-center p-4 overflow-y-auto">
            <div className="w-full max-w-2xl flex flex-col">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4" role="img" aria-hidden>⚠️</div>
                <h2 className="text-3xl font-bold text-orange-400 mb-2">BETA VERSION WARNING</h2>
                <p className="text-xl text-white font-semibold">Fully On-Chain Battle Mode</p>
              </div>

              <div className="bg-orange-900 bg-opacity-30 border border-orange-500 rounded-lg p-5 mb-6">
                <h3 className="text-xl font-bold text-orange-300 mb-4">⚠️ Known Issues:</h3>
                <ul className="space-y-3 text-base text-gray-200">
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">❌</span>
                    <span><strong>Game Engine Bugs:</strong> Damage calculations may not work exactly as intended. Some buff effects may behave unexpectedly.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">❌</span>
                    <span><strong>Transaction Errors:</strong> Submitting turns sometimes fails with "CALL_EXCEPTION" errors. You may need to retry multiple times.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-400 mr-2">⚠️</span>
                    <span><strong>Error Format:</strong> If you encounter errors, check browser console (F12) for full error details.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-900 bg-opacity-30 border border-blue-500 rounded-lg p-5 mb-6">
                <h3 className="text-xl font-bold text-blue-300 mb-3">Recommended:</h3>
                <p className="text-base text-gray-200 mb-2">
                  Use <strong className="text-green-400">Hybrid Mode</strong> for a stable, tested experience with the same game logic.
                </p>
                <p className="text-sm text-gray-300">
                  On-Chain mode is for blockchain maximalists who want full transparency despite potential issues.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <button
                  onClick={() => {
                    setShowOnChainWarning(false)
                    window.dispatchEvent(new CustomEvent('quickbattle:mode-selected', { detail: { mode: 'onchain', data: quickBattleModeData } }))
                  }}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 px-6 rounded-lg transition-all"
                >
                  ⚠️ I Understand, Continue Anyway
                </button>
                <button
                  onClick={() => {
                    setShowOnChainWarning(false)
                    navigateToPage('pveSelection')
                  }}
                  className="flex-1 bg-slate-600 hover:bg-slate-700 text-white font-bold py-4 px-6 rounded-lg transition-all"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}